import {
  GetAllVendorRatesAsCsvResponse,
  GetVendorRatesRequestPath,
  GetVendorRatesResponse,
  ListAllVendorRatesResponse,
} from '@eigtech/tarpulator-types'
import {
  contextualGet,
  queryOptions,
  useQueries,
  useQuery,
  useQueryClient,
  useSuspenseQueries,
  useSuspenseQuery,
} from '@eigtech/ui-shared-api'
import { secondsToMilliseconds } from '@eigtech/ui-shared-dates'
import { tarpulatorBasePath, vendorRatesQueryKeys } from './constants'

const getVendorRatesList = async () => {
  const { vendorRates } = await contextualGet<ListAllVendorRatesResponse>(
    `${tarpulatorBasePath}/vendor-rates/all`
  )
  return vendorRates
}

export const getVendorRatesListQueryOptions = () =>
  queryOptions({
    queryKey: vendorRatesQueryKeys.list(),
    queryFn: () => getVendorRatesList(),
  })

export const useGetVendorRatesList = () => useQuery(getVendorRatesListQueryOptions())

export const useGetVendorRatesListSuspense = () =>
  useSuspenseQuery(getVendorRatesListQueryOptions())

/* ------------------------------------------------------------------------------------------- */

const getVendorRatesDetail = async ({ vendorRatesId }: GetVendorRatesRequestPath) => {
  const { vendorRates } = await contextualGet<GetVendorRatesResponse>(
    `${tarpulatorBasePath}/vendor-rates/${vendorRatesId}`
  )
  return vendorRates
}

export const getVendorRatesDetailQueryOptions = (request: GetVendorRatesRequestPath) =>
  queryOptions({
    queryKey: vendorRatesQueryKeys.detail(request.vendorRatesId),
    queryFn: () => getVendorRatesDetail(request),
  })

export const useGetVendorRatesDetail = (request: GetVendorRatesRequestPath) =>
  useQuery(getVendorRatesDetailQueryOptions(request))

export const useGetVendorRatesDetailSuspense = (request: GetVendorRatesRequestPath) =>
  useSuspenseQuery(getVendorRatesDetailQueryOptions(request))

export const useGetVendorRatesDetails = (requests: GetVendorRatesRequestPath[]) =>
  useQueries({ queries: requests.map((request) => getVendorRatesDetailQueryOptions(request)) })

export const useGetVendorRatesDetailsSuspense = (requests: GetVendorRatesRequestPath[]) =>
  useSuspenseQueries({
    queries: requests.map((request) => getVendorRatesDetailQueryOptions(request)),
  })

/* ------------------------------------------------------------------------------------------- */

const getVendorRatesAsCsvUrl = () =>
  contextualGet<GetAllVendorRatesAsCsvResponse>(`${tarpulatorBasePath}/vendor-rates/all/csv`)

export const getVendorRatesAsCsvUrlQueryOptions = () =>
  queryOptions({
    queryKey: vendorRatesQueryKeys.csv(),
    queryFn: () => getVendorRatesAsCsvUrl(),
    staleTime: secondsToMilliseconds(250),
  })

/* ------------------------------------------------------------------------------------------- */

export function useInvalidateAllVendorRates() {
  const queryClient = useQueryClient()

  return function () {
    queryClient.invalidateQueries({ queryKey: vendorRatesQueryKeys.all() })
  }
}

export function useInvalidateVendorRatesList() {
  const queryClient = useQueryClient()

  return function () {
    queryClient.invalidateQueries({ queryKey: vendorRatesQueryKeys.list() })
  }
}

export function useInvalidateVendorRatesDetail() {
  const queryClient = useQueryClient()

  return function ({ vendorRatesId }: GetVendorRatesRequestPath) {
    queryClient.invalidateQueries({ queryKey: vendorRatesQueryKeys.detail(vendorRatesId) })
  }
}
